import PropTypes from 'prop-types'
import clsx from 'clsx'

import ConceptProgress from '../../../../UI/ConceptProgress'

import { conceptSteps } from './steps'

import styles from './dialog.module.scss'

/**
 * Der Überblick am Kopf des Fensters: Wo steht das Konzept gerade?
 *
 * Vorher stand hier ein Statussatz mit einem Balken darunter. Beides sagt, wie
 * weit es ist — keines von beiden sagt, **was als Nächstes passieren muss und
 * wer dafür am Zug ist**. Genau das ist aber die Frage, mit der die Verwaltung
 * dieses Fenster öffnet: Hängt die Junior Company, und woran?
 *
 * Deshalb drei Dinge in dieser Reihenfolge:
 * 1. der Stand in einem Satz, daneben der Fertigstellungsgrad,
 * 2. die fünf Schritte des Weges mit erledigt/offen und der jeweiligen Zahl,
 * 3. ein Satz „Als Nächstes", der Handlung und Rolle nennt.
 *
 * Die Schritte kommen aus `steps.js` und damit aus denselben Zahlen wie der
 * Balken. Die Daten darunter (Lernstrecke, Schüler:innen, Bewertung, Felder)
 * bleiben, wo sie waren — der Überblick ersetzt sie nicht, er ordnet sie ein.
 */
export default function ConceptOverview({ daten, status, statusText, hinweise = [] }) {
	const { steps, next } = conceptSteps({ progress: daten.progress, state: daten.state, status })

	/*
	 * **Wo das Konzept gar nicht gilt, fehlt der Weg — und das muss dastehen.**
	 *
	 * Für eine Junior Basic Company ist das Konzept in den Einstellungen
	 * abgeschaltet; `evaluateConcept` gibt dann einen verkürzten Zustand zurück,
	 * `conceptProgress` gibt `null`, und die fünf Schritte entfallen. Das ist
	 * richtig — fünf Schritte auf „offen" wären eine Aussage, die niemand
	 * geprüft hat. Ohne Erklärung sah der Kasten aber nur leer aus, und die
	 * naheliegende Deutung war „hier fehlt etwas".
	 */
	const giltNicht = daten.state?.applies === false

	return (
		<section className={styles.overview}>
			<div className={styles.overviewHead}>
				<div className={styles.overviewStatus}>
					<span className={styles.overviewLabel}>Stand</span>
					<b>{statusText}</b>
					{hinweise.map((satz, index) => (
						<span key={index} className={styles.overviewMeta}>
							{satz}
						</span>
					))}
				</div>

				{/*
				 * Derselbe Balken, den Schüler:innen und Lehrkraft sehen — aus
				 * derselben Rechnung im Server. `compact`: Die Beschriftung unter
				 * den vier Stücken stünde hier doppelt, die Schrittliste daneben
				 * sagt dasselbe ausführlicher.
				 */}
				<ConceptProgress compact progress={daten.progress} className={styles.overviewProgress} />
			</div>

			{giltNicht && (
				<p className={styles.next}>
					<b>Nichts zu tun:</b> Für diese Unternehmensform wird das Konzept zurzeit nicht verlangt. Die Junior
					Company kann es trotzdem schreiben; niemand wartet darauf, und es hält nichts auf. Ob es verlangt
					wird, steht in den Einstellungen unter „Checkliste &amp; Voraussetzungen".
				</p>
			)}

			{steps.length > 0 && (
				<>
					<ol className={styles.steps}>
						{steps.map((schritt, index) => (
							<li
								key={schritt.key}
								className={clsx(
									styles.step,
									schritt.done && styles.stepDone,
									schritt.current && styles.stepCurrent
								)}
							>
								{/*
								 * Die Nummer bleibt auch im erledigten Schritt stehen: Sie
								 * ist die Reihenfolge, nicht der Stand. Der Haken sitzt
								 * daneben.
								 */}
								<span className={styles.stepNo}>{index + 1}</span>
								<span className={styles.stepBody}>
									<span className={styles.stepLabel}>{schritt.label}</span>
									<span className={styles.stepMeta}>
										{schritt.who} · {schritt.detail}
									</span>
								</span>
								{/*
								 * Der Stand als Wort und nicht nur als Farbe: Wer die
								 * Abstufung nicht sieht, liest sie.
								 */}
								<span className={styles.stepState}>
									{schritt.done ? 'erledigt' : schritt.current ? 'jetzt dran' : 'offen'}
								</span>
							</li>
						))}
					</ol>

					{next && (
						<p className={styles.next}>
							<b>Als Nächstes:</b> {next}
						</p>
					)}
				</>
			)}
		</section>
	)
}

ConceptOverview.propTypes = {
	daten: PropTypes.shape({
		progress: PropTypes.object,
		state: PropTypes.object
	}).isRequired,
	status: PropTypes.string,
	statusText: PropTypes.string.isRequired,
	// Datumszeilen (abgeschlossen, zurückgegeben, bewertet, freigegeben) —
	// fertige Sätze, damit die Datumsformatierung an einer Stelle bleibt.
	hinweise: PropTypes.arrayOf(PropTypes.string)
}
